import { ref, provide, inject, computed } from 'vue'

const DATE_RANGE_KEY = Symbol('dateRange')

function formatDate(d) {
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function getMonthStart() {
  const now = new Date()
  return formatDate(new Date(now.getFullYear(), now.getMonth(), 1))
}

function getMonthEnd() {
  const now = new Date()
  return formatDate(new Date(now.getFullYear(), now.getMonth() + 1, 0))
}

/**
 * Crée et fournit (provide) le state de date range.
 * A appeler dans le composant parent (DashboardPage).
 */
export function provideDateRange() {
  const dateFrom = ref(getMonthStart())
  const dateTo = ref(getMonthEnd())

  const setRange = (from, to) => {
    dateFrom.value = from
    dateTo.value = to
  }

  const setPreset = (preset) => {
    const now = new Date()
    const today = formatDate(now)

    switch (preset) {
      case 'today':
        setRange(today, today)
        break
      case 'week': {
        const dayOfWeek = now.getDay()
        const diffToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1
        const monday = new Date(now)
        monday.setDate(now.getDate() - diffToMonday)
        const sunday = new Date(monday)
        sunday.setDate(monday.getDate() + 6)
        setRange(formatDate(monday), formatDate(sunday))
        break
      }
      case 'month':
        setRange(getMonthStart(), getMonthEnd())
        break
      case 'quarter': {
        const quarter = Math.floor(now.getMonth() / 3)
        const qStart = new Date(now.getFullYear(), quarter * 3, 1)
        const qEnd = new Date(now.getFullYear(), quarter * 3 + 3, 0)
        setRange(formatDate(qStart), formatDate(qEnd))
        break
      }
      case 'year':
        setRange(
          formatDate(new Date(now.getFullYear(), 0, 1)),
          formatDate(new Date(now.getFullYear(), 11, 31))
        )
        break
    }
  }

  const dateParams = computed(() => ({
    date_from: dateFrom.value,
    date_to: dateTo.value
  }))

  const context = { dateFrom, dateTo, dateParams, setRange, setPreset }

  provide(DATE_RANGE_KEY, context)

  return context
}

/**
 * Injecte le date range depuis le parent.
 * A appeler dans les composants enfants du dashboard.
 */
export function useDateRange() {
  const context = inject(DATE_RANGE_KEY)
  if (!context) {
    throw new Error('useDateRange() doit etre utilise dans un composant enfant de DashboardPage (provide manquant)')
  }
  return context
}
